import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  InputBase,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useState } from 'react';

import { AddFilterListValuePicker } from './AddFilterListValuePicker';
import { FilterFieldDef } from './AddFilterModal.types';
import { AdvancedFilter } from './FilterBar.props';
import { formatDateFilterDisplay, UnifiedDateFilterField } from './UnifiedDateFilterField';
import { normalizeDateFilterValue } from '../../../utils/dateFilterFormat';

interface LabelValueOption {
  label: string;
  value: string;
}

export interface AddFilterConfigureProps {
  field: FilterFieldDef;
  onApply: (filter: AdvancedFilter) => void;
  onBack: () => void;
  /** When provided for a `type: 'list'` field, render an Autocomplete
   *  multiselect instead of the fallback comma-separated text input. */
  options?: LabelValueOption[];
  /** When supplied, the form is seeded with this filter's values and the
   *  Apply button reads "Update Filter". Used for click-to-edit on chips. */
  initialFilter?: AdvancedFilter;
  /** Header chrome. `'modal'` (default) shows a top-left Back button to return
   *  to the field list. `'inline'` (the chip-edit popover) has nowhere to go
   *  back to, so it shows a top-right close (X) button instead. Both invoke
   *  `onBack`. */
  variant?: 'modal' | 'inline';
}

export const AddFilterConfigure: React.FC<AddFilterConfigureProps> = ({
  field,
  onApply,
  onBack,
  options,
  initialFilter,
  variant = 'modal',
}) => {
  const theme = useTheme();
  const isEditing = Boolean(initialFilter);
  const isInline = variant === 'inline';
  const seedFromInitial =
    initialFilter && initialFilter.dataKey === field.key ? initialFilter : undefined;

  // A filter saved before a field's type changed (e.g. Location Zone moving
  // from free text to a list) can carry an operator the field no longer
  // offers. The original predicate still round-trips through buildFilterModel
  // untouched, but on edit we must seed a valid operator so the Select isn't
  // left blank — fall back to the field's first operator.
  const seededOperatorIsValid =
    seedFromInitial != null && field.operators.some((op) => op.value === seedFromInitial.operator);
  const [operator, setOperator] = useState(
    (seededOperatorIsValid ? seedFromInitial?.operator : field.operators[0]?.value) ?? ''
  );
  const [value, setValue] = useState(
    seedFromInitial &&
      field.type !== 'list' &&
      field.type !== 'date' &&
      field.type !== 'boolean' &&
      seedFromInitial.operator !== 'isAnyOf'
      ? (seedFromInitial.value ?? '')
      : ''
  );
  const [value2, setValue2] = useState(seedFromInitial?.value2 ?? '');
  // A date filter value is either a relative code (e.g. `first-day-of-last-month`)
  // or an absolute date. Both share one slot now that the unified picker resolves
  // which the user meant, so there is no separate relative/absolute mode. Legacy
  // ISO `T`-separated datetime values (from the brief PR #420 window) are
  // normalised to the current space-separated wire form on the way in.
  const [dateValueFrom, setDateValueFrom] = useState(
    seedFromInitial?.type === 'date' ? normalizeDateFilterValue(seedFromInitial.dateFrom ?? '') : ''
  );
  const [dateValueTo, setDateValueTo] = useState(
    seedFromInitial?.type === 'date' ? normalizeDateFilterValue(seedFromInitial.dateTo ?? '') : ''
  );
  const [boolValue, setBoolValue] = useState(() => {
    if (seedFromInitial?.type !== 'boolean') return 'true';
    // Presence booleans persist as `isEmpty`/`isNotEmpty` with no value, so
    // reconstruct the Yes/No choice from the operator rather than the value.
    if (field.presence) return seedFromInitial.operator === 'isEmpty' ? 'false' : 'true';
    return seedFromInitial.value ?? 'true';
  });
  const [listValues, setListValues] = useState<string[]>(
    seedFromInitial &&
      seedFromInitial.value &&
      (seedFromInitial.type === 'list' || seedFromInitial.operator === 'isAnyOf')
      ? seedFromInitial.value.split(',').filter(Boolean)
      : []
  );

  // `Is any of` on an otherwise-scalar text/number field (e.g. Serial 1) swaps
  // the single input for the multi-value paste box and emits a comma-joined
  // `isAnyOf` value — the same shape the status / Job ID filters use.
  const isBulkAnyOf = (field.type === 'text' || field.type === 'number') && operator === 'isAnyOf';

  const inputSx = {
    border: `1px solid ${theme.palette.v4.filterInputBorder}`,
    borderRadius: 1,
    px: 1.5,
    py: 0.5,
    fontSize: '0.875rem',
  };

  const getDateValue = (pos: 'from' | 'to'): string =>
    pos === 'from' ? dateValueFrom : dateValueTo;

  const getDateDisplay = (pos: 'from' | 'to'): string =>
    formatDateFilterDisplay(getDateValue(pos), field.withTime);

  const handleApply = () => {
    let display = '';
    let finalValue = value;

    if (field.type === 'boolean' && field.presence) {
      // Presence booleans have no boolean column on the BFF — Yes means "has a
      // value" (`isNotEmpty`) and No means "has none" (`isEmpty`), with no value.
      onApply({
        field: field.name,
        dataKey: field.key,
        type: field.type,
        operator: boolValue === 'true' ? 'isNotEmpty' : 'isEmpty',
        value: '',
        display: boolValue === 'true' ? 'Yes' : 'No',
      });
      return;
    }

    if (field.type === 'boolean') {
      finalValue = boolValue;
      display = boolValue === 'true' ? 'Yes' : 'No';
    } else if (field.type === 'list') {
      finalValue = listValues.join(',');
      const opLabel = field.operators.find((o) => o.value === operator)?.label ?? operator;
      const displayValues = options
        ? listValues.map((v) => options.find((o) => o.value === v)?.label ?? v)
        : listValues;
      display = `${opLabel}: ${displayValues.join(', ')}`;
    } else if (field.type === 'date') {
      const fromVal = getDateValue('from');
      const fromDisplay = getDateDisplay('from');
      const toVal = getDateValue('to');
      const toDisplay = getDateDisplay('to');

      if (operator === 'between') {
        display = `${fromDisplay} to ${toDisplay}`;
      } else if (operator === 'isEmpty' || operator === 'isNotEmpty') {
        display = operator === 'isEmpty' ? 'Is empty' : 'Is not empty';
      } else {
        const opLabel = field.operators.find((o) => o.value === operator)?.label ?? operator;
        display = `${opLabel}: ${fromDisplay}`;
      }

      onApply({
        field: field.name,
        dataKey: field.key,
        type: field.type,
        operator,
        value: fromVal,
        value2: operator === 'between' ? toVal : undefined,
        dateFrom: fromVal,
        dateTo: operator === 'between' ? toVal : undefined,
        display,
      });
      return;
    } else if (isBulkAnyOf) {
      finalValue = listValues.join(',');
      display = `Is any of: ${listValues.join(', ')}`;
    } else if (operator === 'between') {
      display = `${value} to ${value2}`;
    } else {
      const opLabel = field.operators.find((o) => o.value === operator)?.label ?? operator;
      display = `${opLabel}: ${value}`;
    }

    onApply({
      field: field.name,
      dataKey: field.key,
      type: field.type,
      operator,
      value: finalValue,
      value2: operator === 'between' ? value2 : undefined,
      display,
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter') return;
    // Prevent Enter on nested interactive controls (Autocomplete, toggles,
    // selects, buttons) from bubbling up and submitting an incomplete filter.
    const tag = (e.target as HTMLElement).tagName;
    const role = (e.target as HTMLElement).getAttribute('role');
    if (
      tag === 'BUTTON' ||
      tag === 'TEXTAREA' ||
      role === 'button' ||
      role === 'combobox' ||
      role === 'option' ||
      role === 'listbox'
    )
      return;
    handleApply();
  };

  const radioSx = { '&.Mui-checked': { color: theme.palette.v4.accent } };

  const renderOperatorSelect = () => (
    <Select
      value={operator}
      onChange={(e) => setOperator(e.target.value)}
      size="small"
      fullWidth
      sx={{ mb: 1.5 }}
    >
      {field.operators.map((op) => (
        <MenuItem key={op.value} value={op.value}>
          {op.label}
        </MenuItem>
      ))}
    </Select>
  );

  const showDateInputs = operator !== 'isEmpty' && operator !== 'isNotEmpty';

  const fieldSummary = (
    <>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        {field.name}
      </Typography>
      <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
        Type: {field.type}
      </Typography>
    </>
  );

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1 }}
      onKeyDown={handleKeyDown}
    >
      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        {isInline ? (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Box>{fieldSummary}</Box>
            <IconButton
              onClick={onBack}
              size="small"
              aria-label="Close filter editor"
              sx={{ mt: -0.5, mr: -0.5, color: theme.palette.text.secondary }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : (
          <>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={onBack}
              size="small"
              sx={{ textTransform: 'none', color: theme.palette.text.secondary, mb: 1 }}
            >
              Back
            </Button>
            {fieldSummary}
          </>
        )}
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1 }}>
        {field.type === 'boolean' && (
          <RadioGroup value={boolValue} onChange={(e) => setBoolValue(e.target.value)}>
            <FormControlLabel
              value="true"
              control={<Radio size="small" sx={radioSx} />}
              label="Yes"
            />
            <FormControlLabel
              value="false"
              control={<Radio size="small" sx={radioSx} />}
              label="No"
            />
          </RadioGroup>
        )}

        {field.type === 'list' && (
          <>
            {renderOperatorSelect()}
            <AddFilterListValuePicker
              fieldName={field.name}
              values={listValues}
              onChange={setListValues}
              options={options}
              inputSx={inputSx}
            />
          </>
        )}

        {field.type === 'text' && (
          <>
            {renderOperatorSelect()}
            {isBulkAnyOf ? (
              <AddFilterListValuePicker
                fieldName={field.name}
                values={listValues}
                onChange={setListValues}
                inputSx={inputSx}
              />
            ) : (
              operator !== 'isEmpty' && (
                <InputBase
                  placeholder="Enter value..."
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  fullWidth
                  inputProps={{ 'aria-label': `${field.name} value` }}
                  sx={inputSx}
                />
              )
            )}
          </>
        )}

        {field.type === 'number' && (
          <>
            {renderOperatorSelect()}
            {isBulkAnyOf && (
              <AddFilterListValuePicker
                fieldName={field.name}
                values={listValues}
                onChange={setListValues}
                inputSx={inputSx}
              />
            )}
            {!isBulkAnyOf && operator !== 'isEmpty' && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <InputBase
                  placeholder={operator === 'between' ? 'From' : 'Value'}
                  type="number"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  fullWidth
                  inputProps={{ 'aria-label': `${field.name} value` }}
                  sx={inputSx}
                />
                {operator === 'between' && (
                  <InputBase
                    placeholder="To"
                    type="number"
                    value={value2}
                    onChange={(e) => setValue2(e.target.value)}
                    fullWidth
                    inputProps={{ 'aria-label': `${field.name} value 2` }}
                    sx={inputSx}
                  />
                )}
              </Box>
            )}
          </>
        )}

        {field.type === 'date' && (
          <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
            {field.operators.length > 1 && renderOperatorSelect()}

            {showDateInputs && (
              <>
                <UnifiedDateFilterField
                  label={operator === 'between' ? 'From' : 'Date'}
                  value={dateValueFrom}
                  onChange={setDateValueFrom}
                  withTime={field.withTime}
                  ariaLabel={`${field.name} from`}
                />
                {operator === 'between' && (
                  <UnifiedDateFilterField
                    label="To"
                    value={dateValueTo}
                    onChange={setDateValueTo}
                    withTime={field.withTime}
                    ariaLabel={`${field.name} to`}
                  />
                )}
              </>
            )}
          </Box>
        )}
      </Box>

      <Box sx={{ px: 2, py: 1.5, borderTop: `1px solid ${theme.palette.v4.border}` }}>
        <Button
          variant="contained"
          onClick={handleApply}
          fullWidth
          sx={{
            textTransform: 'none',
            bgcolor: theme.palette.v4.accent,
            '&:hover': { bgcolor: theme.palette.v4.accent, opacity: 0.9 },
          }}
        >
          {isEditing ? 'Update Filter' : 'Apply Filter'}
        </Button>
      </Box>
    </Box>
  );
};
